"use client";

// eslint-disable-next-line import/no-named-default
import type { default as Pusher, Channel } from "pusher-js";
import { useContext, createContext, useCallback, useRef } from "react";

export interface PusherContextValues {
  client: Pusher;
  subscribe: (channelName: string) => Channel;
  unsubscribe: (channelName: string) => void;
}

type PusherContextType = PusherContextValues | null;

export const PusherContext = createContext<PusherContextType>(null);

export function usePusher(): PusherContextValues {
  const pusher = useContext(PusherContext);
  if (!pusher) {
    throw new Error("Pusher client not configured");
  }
  return pusher;
}

type ConnectedChannels = Record<string, Channel[]>;

export function PusherProvider({
  children,
  client,
}: {
  children: React.ReactNode;
  client: Pusher;
}) {
  const connectedChannels = useRef<ConnectedChannels>({});

  const subscribe = useCallback(
    (channelName: string) => {
      if (!client || !channelName) {
        throw new Error("Pusher client not configured");
      }

      const pusherChannel = client.subscribe(channelName);
      connectedChannels.current[channelName] = [
        ...(connectedChannels.current[channelName] ?? []),
        pusherChannel,
      ];
      return pusherChannel;
    },
    [client, connectedChannels]
  );

  const unsubscribe = useCallback(
    (channelName: string) => {
      if (
        !connectedChannels.current ||
        !client ||
        !channelName ||
        !(channelName in connectedChannels.current)
      ) {
        return;
      }
      const channels = connectedChannels.current[channelName];
      if (!channels) {
        return;
      }

      if (channels.length === 1) {
        client.unsubscribe(channelName);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete connectedChannels.current[channelName];
      } else {
        channels.pop();
      }
    },
    [connectedChannels, client]
  );

  return (
    <PusherContext.Provider value={{ client, subscribe, unsubscribe }}>
      {children}
    </PusherContext.Provider>
  );
}
